/**
 * FloatingChatbotWidget Component
 * Sticky chatbot launcher icon with compact chatbot window overlay.
 */

import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useLocation } from 'react-router-dom';
import { ROUTES } from '@config/constants';
import { CHATBOT_STRINGS } from '../../constants';
import { ChatWindow } from '../ChatWindow';
import { MessageInput } from '../MessageInput';
import { ChatBubbleIcon, CloseIcon } from '../Icons';
import { useChatbot } from '../../hooks/useChatbot';
import styles from './FloatingChatbotWidget.module.css';

/**
 * Floating chatbot widget component.
 * Renders sticky icon fixed at bottom-right corner.
 * Toggles compact chatbot window on click.
 * Hidden on Login and Registration pages.
 */
export const FloatingChatbotWidget = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const widgetRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    isLoading,
    error,
    sendMessage,
  } = useChatbot();

  // Hide widget on Login and Registration pages
  const isAuthPage =
    location.pathname === ROUTES.LOGIN ||
    location.pathname === ROUTES.REGISTER ||
    location.pathname === ROUTES.FORGOT_PASSWORD;

  useEffect(() => {
    // Close widget when navigating to auth pages
    if (isAuthPage) {
      setIsOpen(false);
    }
  }, [isAuthPage]);

  useEffect(() => {
    // Focus management: move focus to widget when it opens
    if (isOpen && widgetRef.current) {
      widgetRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    // Escape key handler to close widget
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const toggleWidget = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  if (isAuthPage) {
    return null;
  }

  return (
    <>
      {/* Floating Icon Button */}
      <button
        type="button"
        className={styles.floatingButton}
        onClick={toggleWidget}
        aria-label={isOpen ? CHATBOT_STRINGS.ARIA_CLOSE_CHATBOT : CHATBOT_STRINGS.ARIA_OPEN_CHATBOT}
        aria-expanded={isOpen}
      >
        {isOpen ? <CloseIcon size={24} /> : <ChatBubbleIcon size={24} />}
      </button>

      {/* Chatbot Window Overlay */}
      {isOpen && (
        <div
          ref={widgetRef}
          className={styles.widgetContainer}
          role="dialog"
          aria-label={CHATBOT_STRINGS.PAGE_TITLE}
          tabIndex={-1}
        >
          <div className={styles.widgetContent}>
            <ChatWindow
              messages={messages}
              compact={true}
              isLoading={isLoading}
              error={error}
            />
            <MessageInput
              compact={true}
              onSendMessage={sendMessage}
              isLoading={isLoading}
            />
          </div>
        </div>
      )}
    </>
  );
});

FloatingChatbotWidget.displayName = 'FloatingChatbotWidget';
